import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Settings2, ChartBar as BarChart3, FileText, Lock, ChartLine as LineChart, RefreshCw, CircleAlert as AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../lib/useApi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { resolveLogoUrl } from '../../lib/logo';
import ConfigureTab from '../../components/executive-analytics/ConfigureTab';
import AnalyticsTab from '../../components/executive-analytics/AnalyticsTab';
import ReportTab from '../../components/executive-analytics/ReportTab';
import type { Account } from '../../types';
import type { EAWorkspace, EAAnalyticsSnapshot } from '../../types/executiveAnalytics';

type Tab = 'configure' | 'analytics' | 'report';

const RECOMMENDATION_LABELS: Record<string, { label: string; color: string }> = {
  strong_proceed: { label: 'Strong Proceed', color: 'bg-app-positive/10 text-app-positive border-app-positive' },
  proceed_with_conditions: { label: 'Proceed with Conditions', color: 'bg-app-focus/10 text-app-focus border-app-focus/30' },
  needs_more_validation: { label: 'Needs Validation', color: 'bg-app-warning/10 text-app-warning border-app-warning' },
  weak_case: { label: 'Weak Case', color: 'bg-app-warning/10 text-app-warning border-app-warning' },
  do_not_present: { label: 'Do Not Present', color: 'bg-app-critical/10 text-app-critical border-app-critical' },
};

export default function ExecutiveAnalyticsWorkspace() {
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();
  const { organisation } = useAuth();
  const { apiGet } = useApi();
  const [account, setAccount] = useState<Account | null>(null);
  const [workspace, setWorkspace] = useState<EAWorkspace | null>(null);
  const [snapshot, setSnapshot] = useState<EAAnalyticsSnapshot | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('configure');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadWorkspace = useCallback(async () => {
    if (!accountId || !organisation) return;

    let acct: Account;
    try {
      const res = await apiGet<{ account: Account }>(`/v1/account/${accountId}`);
      if (!res.account) throw new Error('not found');
      acct = res.account;
    } catch {
      setError('Account not found');
      setLoading(false);
      return;
    }
    setAccount(acct);

    try {
      const { workspace: ws, snapshot: snap } = await apiGet<{
        workspace: EAWorkspace;
        snapshot: EAAnalyticsSnapshot | null;
      }>(`/v1/ea/account/${accountId}/workspace`);

      setWorkspace(ws);
      if (snap) setSnapshot(snap);

      if (ws.status === 'analytics_ready' || ws.status === 'report_ready') {
        setActiveTab('analytics');
      }
    } catch {
      setError('Failed to load workspace');
      setLoading(false);
      return;
    }

    setLoading(false);
  }, [accountId, organisation, apiGet]);

  useEffect(() => { loadWorkspace(); }, [loadWorkspace]);

  const refreshWorkspace = async () => {
    if (!accountId || !workspace) return;
    try {
      const { workspace: ws, snapshot: snap } = await apiGet<{
        workspace: EAWorkspace;
        snapshot: EAAnalyticsSnapshot | null;
      }>(`/v1/ea/account/${accountId}/workspace`);
      if (ws) setWorkspace(ws);
      if (snap) setSnapshot(snap);
    } catch {
      /* keep current state if the refresh read fails */
    }
  };

  const analyticsLocked = workspace?.configure_status !== 'validated';
  const reportLocked = workspace?.analytics_status !== 'completed';

  const [logoFailed, setLogoFailed] = useState(false);
  const logoSrc = account ? resolveLogoUrl(account.logo_url, account.website_url) : '';

  if (loading) return <div className="flex items-center justify-center h-screen"><LoadingSpinner size="lg" /></div>;

  if (error || !account || !workspace) {
    return (
      <div className="app-page">
        <button onClick={() => navigate('/app/executive-analytics')} className="btn-ghost !px-3 mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </button>
        <div className="text-center py-20">
          <AlertCircle className="w-12 h-12 text-app-critical mx-auto mb-4" />
          <p className="text-app-muted">{error || 'Something went wrong'}</p>
        </div>
      </div>
    );
  }

  const rec = RECOMMENDATION_LABELS[workspace.recommendation] || null;

  return (
    <div className="min-h-screen bg-app-canvas">
      <div className="sticky top-14 z-20 border-b border-app-line bg-app-surface md:top-0">
        <div className="mx-auto max-w-[1440px] px-4 py-4 sm:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/app/executive-analytics')}
                className="p-2 rounded-lg text-app-muted hover:text-app-muted hover:bg-app-canvas transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                {logoSrc && !logoFailed ? (
                  <img src={logoSrc} alt="" className="w-10 h-10 rounded-lg object-cover bg-app-canvas" onError={() => setLogoFailed(true)} />
                ) : (
                  <div className="w-10 h-10 bg-app-brand/10 rounded-lg flex items-center justify-center">
                    <span className="text-lg font-bold text-app-brand">{account.company_name.charAt(0)}</span>
                  </div>
                )}
                <div>
                  <p className="app-kicker mb-1">Executive analysis</p>
                  <h1 className="text-lg font-semibold text-app-ink">{account.company_name}</h1>
                  <div className="flex items-center gap-2 text-xs text-app-muted">
                    <span>{account.industry || 'Unknown Vertical'}</span>
                    {account.is_public_company && <span className="font-mono">({account.ticker_symbol || 'Public'})</span>}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {rec && (
                <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${rec.color}`}>
                  {rec.label}
                </span>
              )}
              {workspace.deal_quality_score > 0 && (
                <div className="text-center px-3">
                  <p className="text-xs text-app-muted">Deal Quality</p>
                  <p className="text-sm font-bold text-app-ink">{Math.round(workspace.deal_quality_score)}%</p>
                </div>
              )}
              {workspace.confidence_score > 0 && (
                <div className="text-center px-3">
                  <p className="text-xs text-app-muted">Confidence</p>
                  <p className="text-sm font-bold text-app-ink">{Math.round(workspace.confidence_score)}%</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mx-auto flex max-w-[1440px] gap-0 overflow-x-auto px-4 sm:px-8" role="tablist" aria-label="Executive analytics workflow">
          {([
            { key: 'configure' as Tab, label: 'Configure', icon: Settings2, locked: false },
            { key: 'analytics' as Tab, label: 'Analytics', icon: BarChart3, locked: analyticsLocked },
            { key: 'report' as Tab, label: 'Report', icon: FileText, locked: reportLocked },
          ]).map(tab => (
            <button
              key={tab.key}
              onClick={() => !tab.locked && setActiveTab(tab.key)}
              disabled={tab.locked}
              role="tab"
              aria-selected={activeTab === tab.key}
              className={`relative flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors
                ${activeTab === tab.key
                  ? 'text-app-brand'
                  : tab.locked
                  ? 'text-app-line-strong cursor-not-allowed'
                  : 'text-app-muted hover:text-app-ink'
                }`}
              title={tab.locked ? `Complete ${tab.key === 'analytics' ? 'Configure' : 'Analytics'} first` : ''}
            >
              {tab.locked ? <Lock className="w-4 h-4" /> : <tab.icon className="w-4 h-4" />}
              {tab.label}
              {activeTab === tab.key && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-app-brand rounded-t" />
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="app-page">
        {activeTab === 'configure' && (
          <ConfigureTab
            account={account}
            workspace={workspace}
            onWorkspaceUpdate={refreshWorkspace}
            onProceedToAnalytics={() => { refreshWorkspace(); setActiveTab('analytics'); }}
          />
        )}
        {activeTab === 'analytics' && (
          <AnalyticsTab
            account={account}
            workspace={workspace}
            snapshot={snapshot}
            onWorkspaceUpdate={refreshWorkspace}
            onProceedToReport={() => { refreshWorkspace(); setActiveTab('report'); }}
            onEditConfigure={() => setActiveTab('configure')}
          />
        )}
        {activeTab === 'report' && (
          <ReportTab
            account={account}
            workspace={workspace}
            snapshot={snapshot}
            onWorkspaceUpdate={refreshWorkspace}
          />
        )}
      </div>
    </div>
  );
}
